// Category pages: a collection page with a category fills itself with every
// live product of that category, plus any hand-picked extras (product_ids).
// Plain functions only, no Supabase calls; the admin screens save the result.

var PLURALS = {
  Briefcase: 'Briefcases',
  Tote: 'Totes',
  Messenger: 'Messengers',
  Backpack: 'Backpacks',
  Wallet: 'Wallets',
  Clutch: 'Clutches',
  Belt: 'Belts',
  Other: 'Other pieces',
};

var DESCRIPTIONS = {
  Briefcase: 'Classic briefcases built to carry your essentials and last a lifetime.',
  Tote: 'Spacious totes in full-grain leather, made for everyday use.',
  Messenger: 'Messenger bags and satchels that wear in beautifully with every journey.',
  Backpack: 'Hand-stitched leather backpacks, made to carry the day in comfort.',
  Wallet: 'Slim wallets and card holders, cut and stitched by hand.',
  Clutch: 'Clutches and purses for evenings out, finished with care.',
  Belt: 'Full-grain leather belts with solid brass buckles.',
  Other: 'One-of-a-kind pieces and small leather goods from the workshop.',
};

export function pluralName(category) {
  var name = String(category || '').trim();
  if (PLURALS[name]) return PLURALS[name];
  if (!name) return PLURALS.Other;
  if (/(s|x|z|ch|sh)$/i.test(name)) return name + 'es';
  return name + 's';
}

export function defaultDescription(category) {
  var name = String(category || '').trim();
  return DESCRIPTIONS[name] || ('Handmade ' + pluralName(name).toLowerCase() + ' in full-grain leather.');
}

function slugify(text) {
  return String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function plain(text) {
  return String(text || '').trim().toLowerCase();
}

// The live products a page shows: every live product of its category, plus
// its hand-picked product_ids that are live, once each, in sort_order.
export function pageProducts(page, products) {
  if (!page) return [];
  var ids = page.product_ids || [];
  var category = page.category || null;
  var seen = {};
  var out = [];
  (products || []).forEach(function(p) {
    if (!p || p.status !== 'live' || seen[p.id]) return;
    if ((category && p.category === category) || ids.indexOf(p.id) >= 0) {
      seen[p.id] = true;
      out.push(p);
    }
  });
  return out
    .map(function(p, i) { return { p: p, i: i }; })
    .sort(function(a, b) { return ((Number(a.p.sort_order) || 0) - (Number(b.p.sort_order) || 0)) || (a.i - b.i); })
    .map(function(x) { return x.p; });
}

// Makes sure every category has exactly one page.
// - A page that already has the category is left exactly as it is.
// - A page with no category whose name or slug is the category (singular or
//   plural, e.g. "Briefcases" or "briefcase") is linked to it: only its
//   category is set, nothing else changes.
// - Otherwise a new Live page is added under Shop.
// Returns { pages, added: [names of new pages], linked: [names of linked pages] }.
export function ensureCategoryPages(pages, categories) {
  var list = (pages || []).slice();
  var added = [];
  var linked = [];
  var maxId = list.reduce(function(m, p) { return typeof p.id === 'number' && p.id > m ? p.id : m; }, 0);
  var maxSort = list.reduce(function(m, p) { return Math.max(m, Number(p.sort_order) || 0); }, 0);

  (categories || []).forEach(function(category) {
    if (list.some(function(p) { return p.category === category; })) return;

    var plural = pluralName(category);
    var names = [plain(category), plain(plural)];
    var slugs = [slugify(category), slugify(plural)];
    var at = list.findIndex(function(p) {
      if (p.category) return false;
      return names.indexOf(plain(p.name)) >= 0 || slugs.indexOf(p.slug) >= 0;
    });
    if (at >= 0) {
      list[at] = Object.assign({}, list[at], { category: category });
      linked.push(list[at].name);
      return;
    }

    var usedSlugs = list.map(function(p) { return p.slug; });
    var base = slugify(plural);
    var slug = base;
    for (var n = 2; usedSlugs.indexOf(slug) >= 0; n += 1) slug = base + '-' + n;
    maxId += 1;
    maxSort += 1;
    list.push({
      id: maxId,
      name: plural,
      slug: slug,
      eyebrow: 'The collection',
      description: defaultDescription(category),
      parent_nav: 'Shop',
      product_ids: [],
      sort_order: maxSort,
      status: 'live',
      category: category,
    });
    added.push(plural);
  });

  return { pages: list, added: added, linked: linked };
}

// One line for a toast or summary, e.g. "Added 6 pages: Messengers, Wallets, …".
export function categoryPagesText(result) {
  var parts = [];
  if (result.added.length) {
    parts.push('Added ' + result.added.length + ' page' + (result.added.length !== 1 ? 's' : '') + ': ' + result.added.join(', ') + '.');
  }
  if (result.linked.length) {
    parts.push('Linked ' + result.linked.join(', ') + ' to ' + (result.linked.length === 1 ? 'its category' : 'their categories') + '.');
  }
  if (!parts.length) return 'All categories already have a page';
  return parts.join(' ');
}
